import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography
} from '@mui/material';
import { detectInputMode } from './DetectMode';
import FactCheckInput from './FactCheckInput';
import FactCheckDisplay from './FactCheckDisplay';

const FACTCHECK_MARKER = ':::DEEPSEEK:::';
const allowedDocTypes = [
  'text/plain',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

const VALID_MODES = new Set(['generic', 'ai']);

const safeParse = (maybe) => {
  if (maybe === null || maybe === undefined) return null;
  if (typeof maybe === 'object') return maybe;
  if (typeof maybe === 'string') { try { return JSON.parse(maybe); } catch { return null; } }
  return null;
};

const makeShort = (t = '', maxWords = 30) => {
  const words = String(t || '').trim().split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return words.join(' ');
  return words.slice(0, maxWords).join(' ') + ' …';
};

/** Hardened Gemini extractor */
const extractGeminiText = (anyResp) => {
  if (!anyResp) return '';
  let resp = anyResp;
  if (typeof resp === 'string') { try { resp = JSON.parse(resp); } catch { return ''; } }
  let root = resp.ai_response ?? resp;
  if (typeof root === 'string') { try { root = JSON.parse(root); } catch {} }

  const out = [];

  const cands = root?.candidates;
  if (Array.isArray(cands)) {
    for (const c of cands) {
      const parts = c?.content?.parts;
      if (Array.isArray(parts)) {
        for (const part of parts) {
          const t = part?.text;
          if (typeof t === 'string' && t.trim()) out.push(t.trim());
        }
      }
    }
  }

  if (!out.length) {
    const parts = root?.content?.parts;
    if (Array.isArray(parts)) {
      for (const part of parts) {
        const t = part?.text;
        if (typeof t === 'string' && t.trim()) out.push(t.trim());
      }
    }
  }

  if (!out.length && typeof root?.text === 'string' && root.text.trim()) {
    out.push(root.text.trim());
  }

  return out.join('\n\n').trim();
};

const generateClaimSummary50 = (text = '') => {
  let s = String(text).replace(/```[\s\S]*?```/g, '').replace(/`([^`]+)`/g, '$1').trim();
  const words = s.split(/\s+/);
  if (words.length > 1000) return words.slice(0, 1000).join(' ') + ' …';
  return s;
};

const FactCheckComponent = ({
  claim,
  setClaim,
  result,
  loading,
  handleCheckClaim,
  darkMode,
  /** needed for voting + feedback **/
  user,
}) => {
  const [submittedClaim, setSubmittedClaim] = useState('');
  const [showResults, setShowResults] = useState(false);

  // Advanced analysis renders inside Summary
  const [advancedAnalysis, setAdvancedAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  const [modeConfirmationOpen, setModeConfirmationOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');

  const [citationOpen, setCitationOpen] = useState(false);

  const [wordLimitAlertOpen, setWordLimitAlertOpen] = useState(false);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');

  // local lock while submitting
  const [submitting, setSubmitting] = useState(false);

  const [inputMode, setInputMode] = useState('generic'); // 'generic' | 'ai'
  const [delayedMessageShown, setDelayedMessageShown] = useState(false);

  // track live submit -> so history ignores toggle
  const [lastSubmittedWasAi, setLastSubmittedWasAi] = useState(false);
  const [lastSubmittedClaimId, setLastSubmittedClaimId] = useState(null);

  const attachmentInputRef = useRef();
  const imageInputRef = useRef();
  const topRef = useRef(null);
  const resultsRef = useRef(null);

  // NEW: keep selected files so we can upload them
  const [attachedTextFile, setAttachedTextFile] = useState(null);   // File | null
  const [attachedImageFile, setAttachedImageFile] = useState(null); // File | null

  const HEADER_HEIGHT = 64;
  const WELCOME_HEIGHT = 0;
  const LEFT_PANEL_WIDTH = 320;

  const INPUT_HEIGHT = 72;
  const FOOTER_HEIGHT = 72;
  const GAP_ABOVE_FOOTER = 10;
  const EXTRA_COMPOSER_OFFSET = 6;

  const COMPOSER_BOTTOM = FOOTER_HEIGHT + GAP_ABOVE_FOOTER + EXTRA_COMPOSER_OFFSET;
  const RESULTS_BOTTOM_GAP = FOOTER_HEIGHT + INPUT_HEIGHT + GAP_ABOVE_FOOTER + EXTRA_COMPOSER_OFFSET;

  const [showScrollToTop, setShowScrollToTop] = useState(false);

  // —— Normalize server result -> parsedResult
  const parsedResult = useMemo(() => {
    if (!result) return null;
    const r = typeof result === 'string' ? safeParse(result) ?? { raw: result } : result;

    const claimObj  = r.claim ?? r.claim_data ?? r.claims ?? r.raw?.claim ?? null;
    const factCheck = r.fact_check ?? r.factCheck ?? r.raw?.fact_check ?? r.raw?.fact_check_data ?? null;
    const userAi    = r.user_ai_result ?? r.userAiResult ?? r.raw?.user_ai_result ?? r.raw?.user_ai ?? null;

    const apiRespUnified =
      (userAi && (userAi.api_response_parsed ?? safeParse(userAi.api_response))) ??
      r.api_response_parsed ??
      safeParse(r.api_response) ??
      r.apiResp ??
      r.raw?.api_response_parsed ??
      safeParse(r.raw?.api_response) ??
      null;

    const aiText = extractGeminiText(apiRespUnified);
    const hasAiResponse = !!aiText;

    const claim_text =
      claimObj?.claim_text ?? claimObj?.text ?? r.claim_text ?? r.text ?? (typeof r === 'string' ? r : '') ?? '';

    const summary =
      r.claim_summary ?? r.summary ??
      (apiRespUnified && (apiRespUnified.summary ?? apiRespUnified.claimSummary)) ??
      (apiRespUnified?.wikipedia?.title ? apiRespUnified.wikipedia.title : null) ??
      (apiRespUnified?.wikipedia?.extract ? makeShort(apiRespUnified.wikipedia.extract, 30) : null) ??
      makeShort(claim_text, 30) ?? '';

    const verdict =
      (factCheck && (factCheck.result ?? factCheck.verdict ?? factCheck.truth_rating)) ??
      (apiRespUnified && (apiRespUnified.verdict ?? apiRespUnified.truthRating)) ??
      r.verdict ?? null;

    const confidence =
      (factCheck && (factCheck.confidence ?? factCheck.confidence_pct)) ??
      (apiRespUnified && apiRespUnified.confidence) ??
      r.confidence ?? null;

    const wikipedia =
      r.wikipedia ?? r.wiki ?? apiRespUnified?.wikipedia ?? (factCheck && factCheck.wikipedia) ?? null;

    const newsArticles =
      r.news_articles ?? r.news ?? apiRespUnified?.newsArticles ?? apiRespUnified?.news ?? r.newsArticles ?? [];

    const googleFactCheck =
      apiRespUnified?.googleFactCheck ?? r.googleFactCheck ?? r.raw?.google_fact_check ?? null;

    return {
      claim_id: r.claim_id ?? r.claimId ?? claimObj?.id ?? claimObj?.claim_id ?? null,
      id: r.id ?? claimObj?.id ?? null,
      claim: claimObj ?? null,
      claimText: claim_text,
      summary,
      verdict,
      confidence,
      wiki: wikipedia,
      news: newsArticles,
      googleFactCheck,
      apiResp: apiRespUnified,
      hasFactCheck: Boolean(factCheck),
      hasAiResponse,
      aiText,
      raw: r
    };
  }, [result]);

  // Derived
  const requestBusy = loading || submitting;
  const thisClaimId = parsedResult?.claim_id ?? parsedResult?.id ?? null;
  const dataReady = !requestBusy && !!thisClaimId;

  // remember last live submit id
  useEffect(() => {
    if (dataReady && submittedClaim && thisClaimId && !lastSubmittedClaimId) {
      setLastSubmittedClaimId(thisClaimId);
    }
  }, [dataReady, submittedClaim, thisClaimId, lastSubmittedClaimId]);

  // clear when mode flips
  useEffect(() => {
    setSubmittedClaim('');
    setAdvancedAnalysis(null);
    setShowResults(false);
    setClaim('');
    // NEW: also clear any queued files on mode change
    setAttachedTextFile(null);
    setAttachedImageFile(null);
  }, [inputMode, setClaim]);

  // control “thinking” vs results
  useEffect(() => {
    if (loading) setShowResults(false);
    else if (parsedResult?.claim_id || parsedResult?.id) setShowResults(true);
  }, [loading, parsedResult?.claim_id, parsedResult?.id]);

  // show back-to-top after a bit of scroll
  useEffect(() => {
    const el = resultsRef.current;
    if (!el) return;
    const onScroll = () => setShowScrollToTop(el.scrollTop > 300);
    el.addEventListener('scroll', onScroll);
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, [resultsRef]);

  // delayed “still working”
  useEffect(() => {
    let t;
    if (requestBusy) t = setTimeout(() => setDelayedMessageShown(true), 45000);
    else setDelayedMessageShown(false);
    return () => clearTimeout(t);
  }, [requestBusy]);

  // ===== Uploads (wired to FactCheckInput) =====
  const processTextFileToClaim = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const summary = generateClaimSummary50(String(e.target.result || ''));
      if (summary.endsWith(' …')) setWordLimitAlertOpen(true);
      setClaim(summary);
      setShowResults(false);
    };
    reader.readAsText(file);
  };

  const handleAttachmentSelected = (file) => {
    if (!file) return;
    if (file.type === 'text/plain') {
      processTextFileToClaim(file);
      // NEW: keep for upload
      setAttachedTextFile(file);
    } else if (allowedDocTypes.includes(file.type)) {
      setSnackSeverity('info');
      setSnackMessage('For now the composer ingests only .txt files. PDF/DOC parsing coming soon.');
      setSnackOpen(true);
      // still keep the original file if it's a text/plain by extension edge case handled above
    } else {
      setSnackSeverity('warning');
      setSnackMessage('Unsupported file type.');
      setSnackOpen(true);
    }
  };

  const handleImageSelected = (file) => {
    if (!file) return;
    setSnackSeverity('success');
    setSnackMessage(`Image attached: ${file.name}`);
    setSnackOpen(true);
    // NEW: keep for upload
    setAttachedImageFile(file);
  };

  // ===== Submit =====
  const onSubmit = (forcedMode) => {
    if (!claim || !claim.trim()) return;
    let mode = forcedMode || inputMode || detectInputMode(claim);
    if (!VALID_MODES.has(mode)) mode = detectInputMode(claim) || 'generic';

    const toSend = mode === 'ai' ? claim + FACTCHECK_MARKER : claim;

    setSubmittedClaim(toSend);
    setLastSubmittedWasAi(mode === 'ai');
    setLastSubmittedClaimId(null);
    setAdvancedAnalysis(null);
    setShowResults(false);
    setSubmitting(true);

    // NEW: if any file is present, send multipart/form-data
    if (attachedTextFile || attachedImageFile) {
      const form = new FormData();
      form.append('claim_text', toSend);
      if (user?.id) form.append('user_id', String(user.id));
      // keep the same field names your PHP accepts
      if (attachedTextFile) form.append('attachment', attachedTextFile);
      if (attachedImageFile) form.append('image', attachedImageFile);

      // let your existing handler post the FormData (no need to set headers; browser does it)
      handleCheckClaim(form);
    } else {
      // original JSON path
      handleCheckClaim(toSend);
    }

    setClaim('');
  };

  useEffect(() => {
    if (!loading) setSubmitting(false);
  }, [loading]);

  const handleDetectMode = () => {
    if (VALID_MODES.has(inputMode)) { onSubmit(inputMode); return; }
    const d = detectInputMode(claim);
    if (d === 'unknown') setModeConfirmationOpen(true); else onSubmit(d);
  };

  // ===== Advanced Analysis -> Summary + persist to DB =====
  const handleAdvancedAnalysis = async () => {
    const effectiveClaim = submittedClaim || parsedResult?.claimText || '';
    if (!parsedResult || !effectiveClaim) {
      setSnackSeverity('warning');
      setSnackMessage("Claim and result are required for advanced analysis.");
      setSnackOpen(true);
      return;
    }

    setAnalysisLoading(true);
    try {
      const getKey = "AIzaSyDfkM1jBbxiu5VLMuyUvh0sDFJRXW10X_s";
      const prompt = `
You are a fact-checking assistant. Analyze the following claim and accompanying fact-checking result.
Provide a concise and insightful analysis in plain language.

Claim:
${effectiveClaim}

Fact Check Result:
${JSON.stringify(parsedResult, null, 2)}
      `.trim();

      const payload = { contents: [{ role: "user", parts: [{ text: prompt }] }] };
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${getKey}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }
      );
      if (!response.ok) throw new Error("Failed to fetch analysis");

      const data = await response.json();
      const generatedText = data?.candidates?.[0]?.content?.parts?.[0]?.text;
      if (!generatedText) throw new Error("No analysis returned by Gemini");

      setAdvancedAnalysis({ details: generatedText });

      // persist to DB if we have a claim id
      const claimId = parsedResult?.claim_id || parsedResult?.id || parsedResult?.claim?.id;
      if (claimId) {
        try {
          await fetch('https://www.xfactual.org/api/update_advanced_analysis.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ claim_id: claimId, advanced_analysis: generatedText })
          });
        } catch {}
      }

      setSnackSeverity('success');
      setSnackMessage("Advanced analysis complete.");
      setSnackOpen(true);
    } catch (error) {
      console.error("Advanced analysis error:", error);
      setSnackSeverity('error');
      setSnackMessage("Advanced analysis failed: " + (error.message || error));
      setSnackOpen(true);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const handleScrollToTop = () => {
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // ===== Share =====
  const handleShare = async () => {
    const text = parsedResult?.summary || parsedResult?.claimText || '';
    const url =
      (parsedResult?.news?.[0]?.url || parsedResult?.news?.[0]?.link) ||
      (parsedResult?.wiki?.url) ||
      window.location.href;

    try {
      if (navigator.share) {
        await navigator.share({ title: 'XFactual', text, url });
      } else {
        await navigator.clipboard.writeText(`${text}\n${url}`);
        setSnackSeverity('success');
        setSnackMessage('Link copied to clipboard.');
        setSnackOpen(true);
      }
    } catch {}
  };

  // ===== Voting (server + graceful fallback) =====
  const updateVote = async (voteValue /* 1 = up, 0 = down */) => {
    const claimId =
      parsedResult?.claim_id || parsedResult?.id || parsedResult?.claim?.id;
    if (!claimId || !user?.id) {
      // fallback to local-only acknowledgement
      setSnackSeverity('info');
      setSnackMessage('Thanks! (Sign in to record your vote.)');
      setSnackOpen(true);
      return;
    }

    try {
      const res = await fetch('https://www.xfactual.org/api/update_vote.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ claim_id: claimId, user_id: user.id, vote: voteValue })
      });
      const data = await res.json().catch(() => ({}));
      if (data?.error) {
        setSnackSeverity('error');
        setSnackMessage('Vote update failed: Please login to vote');
      } else {
        setSnackSeverity('success');
        setSnackMessage(data?.message || 'Vote recorded. Thank you!');
      }
      setSnackOpen(true);
    } catch (err) {
      setSnackSeverity('error');
      setSnackMessage('Network error during vote update: ' + err.message);
      setSnackOpen(true);
    }
  };

  const handleThumbsUp = () => updateVote(1);
  const handleThumbsDown = () => updateVote(0);

  // ===== Citations =====
  const getCitationText = () => {
    const articles = parsedResult?.news || parsedResult?.newsArticles || [];
    if (!Array.isArray(articles) || articles.length === 0) return 'No citations available.';

    return articles.map((a) => {
      const title = a?.title || '';
      const source = a?.source || a?.source_name || '';
      const url = a?.url || a?.link || '';
      const dateStr = a?.published_at || a?.publishedAt || new Date().toISOString();
      const date = new Date(dateStr).toLocaleDateString();
      return `${title}. ${source}. Retrieved ${date}, from ${url}`;
    }).join('\n\n');
  };

  const handleCopyCitations = async () => {
    try {
      await navigator.clipboard.writeText(getCitationText());
      setSnackSeverity('success');
      setSnackMessage('Citations copied.');
    } catch {
      setSnackSeverity('error');
      setSnackMessage('Copy failed.');
    }
    setSnackOpen(true);
  };

  const handleDownloadCitations = () => {
    const citationText = getCitationText();
    const blob = new Blob([citationText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'citations.txt';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // ===== Full response download =====
  const handleDownloadFullResponse = () => {
    const filename = (submittedClaim.trim() || 'fact_check').replace(/[^a-zA-Z0-9]/g, '_');
    const sections = [];

    sections.push(`=== Claim ===\n${submittedClaim || '(no claim)'}\n`);

    if (parsedResult) {
      if (parsedResult.aiText) {
        sections.push(`=== AI Response ===\n${parsedResult.aiText}\n`);
      }
      if (parsedResult.verdict) {
        sections.push(`Verdict: ${String(parsedResult.verdict).toUpperCase()}`);
      }
      if (parsedResult.confidence) {
        sections.push(`Confidence: ${parsedResult.confidence}`);
      }
      const just =
        parsedResult?.raw?.fact_check?.justification ??
        parsedResult?.apiResp?.justification ??
        '';
      if (just) sections.push(`Justification:\n${typeof just === 'object' ? JSON.stringify(just, null, 2) : just}`);

      if (parsedResult.wiki) {
        sections.push(`Wikipedia:\nTitle: ${parsedResult.wiki.title || ''}\nSnippet: ${parsedResult.wiki.extract || ''}`);
      }

      const articles = parsedResult?.news || parsedResult?.newsArticles || [];
      if (articles.length) {
        const newsTxt = articles.map((a, i) => {
          const when = a.published_at || a.publishedAt || '';
          const whenFmt = when ? new Date(when).toLocaleDateString() : '';
          return `Article ${i + 1}:\nTitle: ${a.title || ''}\nDescription: ${a.description || a.excerpt || ''}\nSource: ${a.source || ''}\nPublished: ${whenFmt}\nURL: ${a.url || a.link || ''}`;
        }).join('\n\n');
        sections.push('News Articles:\n' + newsTxt);
      }
    } else {
      sections.push('No fact-check result available.');
    }

    const fullText = sections.join('\n\n');
    const blob = new Blob([fullText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // decide AI vs Claim for view
  const viewingLastSubmitted =
    lastSubmittedWasAi && !!lastSubmittedClaimId && thisClaimId === lastSubmittedClaimId;

  const aiErrored =
    !!(parsedResult?.apiResp?.error ||
       parsedResult?.raw?.user_ai_result?.api_response_parsed?.error ||
       parsedResult?.raw?.user_ai_result?.api_response?.error);
  const hasUsableAi = !!parsedResult?.aiText && !aiErrored;

  const isAiOnlyForChild = viewingLastSubmitted ? (inputMode === 'ai') : hasUsableAi;

  return (
    <>
      {/* Mode chooser */}
      <Dialog open={modeConfirmationOpen} onClose={() => setModeConfirmationOpen(false)}>
        <DialogTitle>Process as</DialogTitle>
        <DialogActions>
          <Button onClick={() => { setModeConfirmationOpen(false); onSubmit('generic'); }}>Claim</Button>
          <Button variant="contained" onClick={() => { setModeConfirmationOpen(false); onSubmit('ai'); }}>AI</Button>
        </DialogActions>
      </Dialog>

      {/* Snack */}
      <Snackbar open={snackOpen} autoHideDuration={4000} onClose={() => setSnackOpen(false)}>
        <Alert severity={snackSeverity}>{snackMessage}</Alert>
      </Snackbar>

      {/* Results area */}
      <Box
        component="main"
        ref={resultsRef}
        sx={{
          position: 'absolute',
          top: `calc(${HEADER_HEIGHT}px + ${WELCOME_HEIGHT}px)`,
          left: LEFT_PANEL_WIDTH,
          right: 0,
          bottom: RESULTS_BOTTOM_GAP,
          overflowY: 'auto',
          zIndex: 0
        }}
      >
        {/* Thinking (animated gradient) */}
        {requestBusy && (
          <Box
            sx={{
              p: 3,
              pt: 10,
              textAlign: 'center',
              '@keyframes shine': {
                '0%':   { backgroundPosition: '0% 50%' },
                '50%':  { backgroundPosition: '100% 50%' },
                '100%': { backgroundPosition: '0% 50%' },
              },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                display: 'inline-block',
                fontWeight: 800,
                letterSpacing: 0.2,
                backgroundImage: 'linear-gradient(90deg,#ff4d4d,#ffa64d,#ffe14d,#59ff4d,#4dd9ff,#7d4dff,#ff4dd2)',
                backgroundSize: '200% 100%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'shine 2.2s linear infinite',
              }}
            >
              Working…
            </Typography>

            {delayedMessageShown && (
              <Typography
                variant="subtitle2"
                sx={{
                  mt: 1,
                  fontWeight: 'bold',
                  background: 'linear-gradient(90deg, red,orange,yellow,green,blue,indigo,violet)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundSize: '200% 100%',
                  animation: 'shine 2.2s linear infinite',
                }}
              >
                Still working… one moment 💫
              </Typography>
            )}
          </Box>
        )}
        {/* Results */}
        {dataReady && (
          <Box sx={{ p: 2, pb: 18 }}>
            <div ref={topRef} />
            <FactCheckDisplay
              parsedResult={parsedResult}
              darkMode={darkMode}
              topRef={topRef}
              showScrollToTop={showScrollToTop}
              handleScrollToTop={handleScrollToTop}
              claim={submittedClaim}
              /* voting */
              handleThumbsUp={handleThumbsUp}
              handleThumbsDown={handleThumbsDown}
              thumbsUpCount={0}
              thumbsDownCount={0}
              showResults={showResults}
              aiOnly={isAiOnlyForChild}
              showSummary
              advancedAnalysis={advancedAnalysis}
              analysisLoading={analysisLoading}
              onAdvancedAnalysis={handleAdvancedAnalysis}
              onRegenerate={() => onSubmit(inputMode)}
              onShare={handleShare}
            />
          </Box>
        )}
      </Box>

      {/* Composer */}
      <Box
        sx={{
          position: 'fixed',
          bottom: `${COMPOSER_BOTTOM}px`,
          left: LEFT_PANEL_WIDTH,
          right: 0,
          height: INPUT_HEIGHT,
          borderColor: 'divider',
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          p: 1,
          bgcolor: darkMode ? '#111' : '#fff',
        }}
      >
        <FactCheckInput
          claim={claim}
          setClaim={setClaim}
          handleDetectMode={handleDetectMode}
          handleDrop={(e) => {
            e.preventDefault();
            const files = Array.from(e.dataTransfer.files);
            const txt = files.find(f => f.type === 'text/plain');
            if (txt) handleAttachmentSelected(txt);
            else if (files.length) setSnackMessage('Drop a .txt file to ingest.'); // gentle hint
          }}
          handleDragOver={(e) => e.preventDefault()}
          loading={requestBusy}
          darkMode={darkMode}
          aiMode={inputMode === 'ai'}
          onModeChange={(on) => setInputMode(on ? 'ai' : 'generic')}
          attachmentInputRef={attachmentInputRef}
          imageInputRef={imageInputRef}
          setShowResults={setShowResults}
          setAdvancedAnalysis={setAdvancedAnalysis}
          allowedDocTypes={allowedDocTypes}
          /* NEW wires */
          onAttachmentSelected={handleAttachmentSelected}
          onImageSelected={handleImageSelected}
          onBackToTop={handleScrollToTop}
        />
      </Box>

      {/* Feedback Dialog */}
      <Dialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} disableEnforceFocus>
        <DialogTitle>Submit Feedback</DialogTitle>
        <DialogContent sx={{ width: { xs: '90vw', md: '600px' } }}>
          <TextField
            autoFocus
            multiline
            minRows={8}
            fullWidth
            variant="outlined"
            placeholder="Enter your feedback here..."
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            inputProps={{ 'aria-label': 'Feedback text input' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeedbackOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                const apiUrl = process.env.REACT_APP_API_URL
                  ? `${process.env.REACT_APP_API_URL}/feed_back.php`
                  : 'https://www.xfactual.org/api/feed_back.php';
                const uid = user?.id || 2;
                const payload = { fact_check_id: null, user_id: uid, feedback_text: feedbackText };
                const res = await fetch(apiUrl, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(payload),
                });
                const data = await res.json().catch(() => ({}));
                if (res.ok) {
                  setSnackSeverity('success');
                  setSnackMessage('Feedback submitted successfully!');
                } else {
                  setSnackSeverity('error');
                  setSnackMessage('Error submitting feedback: ' + (data?.error || 'Unknown error'));
                }
              } catch (err) {
                setSnackSeverity('error');
                setSnackMessage('Network error during feedback submission: ' + err.message);
              } finally {
                setSnackOpen(true);
                setFeedbackOpen(false);
                setFeedbackText('');
              }
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Citations Dialog */}
      <Dialog open={citationOpen} onClose={() => setCitationOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Article Citations (AP format)</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            variant="outlined"
            multiline
            minRows={6}
            value={getCitationText()}
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDownloadCitations}>Download Citations</Button>
          <Button onClick={handleCopyCitations}>Copy Citations</Button>
          <Button onClick={() => setCitationOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Word Limit Alert */}
      <Dialog open={wordLimitAlertOpen} onClose={() => setWordLimitAlertOpen(false)}>
        <DialogTitle>Notice</DialogTitle>
        <DialogContent>
          <Typography>
            Only the first 1000 words of the uploaded document will be used in the prompt.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWordLimitAlertOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FactCheckComponent;

